/**
 * AgentSwitcherContent — guide switcher drawer body.
 *
 * Same drawer UI as Language/Explore/etc. (the Drawer host provides the title +
 * close chrome); this is just the search + guide rows + quick picks. Mirrors
 * RN's AgentSelector. Selecting routes through useEnsureDestinationSession /
 * selectSession so the session inits match the app, then closes the drawer.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useChatSessions } from '../../hooks/useChatSessions';
import { useEnsureDestinationSession } from '../../hooks/useEnsureDestinationSession';
import { useGuideCatalog } from '../../hooks/useGuideCatalog';
import { useDrawer } from '../../context/DrawerContext';
import { useLanguage } from '../../hooks/useLanguage';
import Spinner from '../../common/Spinner';
import './AgentSwitcherContent.css';

const AgentSwitcherContent: React.FC<{ visible?: boolean }> = ({
  visible = true,
}) => {
  const { t } = useLanguage();
  const { sessions, selectedSessionId, selectSession, listSessions } =
    useChatSessions();
  const { ensureSession, ensureByAgent, isWorking } =
    useEnsureDestinationSession();
  // DB-driven roster (geo guides + experience overlays). Degrades to [] on a
  // missing/older backend, so the switcher still works off existing sessions.
  const { guides: catalog } = useGuideCatalog(visible);
  const { closeDrawer } = useDrawer();
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (visible) listSessions().catch(() => {});
  }, [visible, listSessions]);

  const q = query.trim().toLowerCase();
  // Dedupe "Your guides" by agent. A user can accumulate many sessions on the
  // same guide (e.g. repeated default-guide sessions), and listing every
  // session makes the same guide appear many times. Keep one row per agent
  // (the first — the list arrives most-recent-first).
  const guides = useMemo(() => {
    const seen = new Set<string>();
    const oncePerAgent = sessions.filter(s => {
      if (!s.agentId || seen.has(s.agentId)) return false;
      seen.add(s.agentId);
      return true;
    });
    return oncePerAgent.filter(
      s =>
        !q ||
        (s.agentDisplayName || '').toLowerCase().includes(q) ||
        (s.title || '').toLowerCase().includes(q),
    );
  }, [sessions, q]);

  // Catalog guides the user doesn't already have a session for, split by kind
  // and filtered by the search box (name/locale/description).
  const owned = useMemo(
    () => new Set(sessions.map(s => s.agentId).filter(Boolean) as string[]),
    [sessions],
  );
  const matchesQuery = useCallback(
    (name?: string | null, sub?: string | null) =>
      !q ||
      (name || '').toLowerCase().includes(q) ||
      (sub || '').toLowerCase().includes(q),
    [q],
  );
  const geoSuggestions = useMemo(
    () =>
      catalog.filter(
        g =>
          g.kind === 'geo' &&
          !owned.has(g.agent_id) &&
          matchesQuery(g.name, g.locale),
      ),
    [catalog, owned, matchesQuery],
  );
  const overlaySuggestions = useMemo(
    () =>
      catalog.filter(
        g =>
          g.kind === 'overlay' &&
          !owned.has(g.agent_id) &&
          matchesQuery(g.name, g.description),
      ),
    [catalog, owned, matchesQuery],
  );

  async function pickExisting(id: string) {
    await selectSession(id);
    closeDrawer();
  }

  async function pickGuide(agentId: string, title: string) {
    const res = await ensureByAgent(agentId, title);
    if (res.sessionId) {
      setQuery('');
      closeDrawer();
    }
  }

  async function addDestination(search: string) {
    const trimmed = search.trim();
    if (!trimmed) return;
    const res = await ensureSession(trimmed);
    if (res.sessionId) {
      setQuery('');
      closeDrawer();
    }
  }

  const nothingToShow =
    guides.length === 0 &&
    geoSuggestions.length === 0 &&
    overlaySuggestions.length === 0;

  return (
    <div className="wg-agentsw">
      <div className="wg-agentsw__search-wrap">
        <SearchIcon />
        <input
          className="wg-agentsw__search"
          type="search"
          placeholder={t('AGENT_SELECTOR_SEARCH') || 'Search a destination'}
          value={query}
          onChange={e => setQuery(e.target.value)}
        />
        {query && (
          <button
            type="button"
            className="wg-agentsw__clear"
            onClick={() => setQuery('')}
            aria-label="Clear search"
          >
            ×
          </button>
        )}
      </div>

      {guides.length > 0 && (
        <section className="wg-agentsw__group">
          <div className="wg-agentsw__group-title">
            {t('AGENT_SELECTOR_TITLE') || 'Your guides'}
          </div>
          {guides.map(g => {
            const active = g.id === selectedSessionId;
            return (
              <button
                key={g.id}
                type="button"
                className={`wg-agentsw__row${active ? ' is-selected' : ''}`}
                onClick={() => pickExisting(g.id)}
                data-testid={`guide-${g.agentId}`}
              >
                <span className="wg-agentsw__avatar">
                  {g.agentAvatarUrl ? (
                    <img src={g.agentAvatarUrl} alt="" />
                  ) : (
                    <span className="wg-agentsw__avatar-fallback">
                      {(g.agentDisplayName || 'G').slice(0, 1)}
                    </span>
                  )}
                </span>
                <span className="wg-agentsw__text">
                  <span className="wg-agentsw__name">
                    {g.agentDisplayName || t('AGENT_FALLBACK_NAME') || 'Guide'}
                  </span>
                  {!!g.title && (
                    <span className="wg-agentsw__sub">{g.title}</span>
                  )}
                </span>
                <span className="wg-agentsw__state">
                  {active ? <CheckIcon /> : null}
                </span>
              </button>
            );
          })}
        </section>
      )}

      {geoSuggestions.length > 0 && (
        <section className="wg-agentsw__group">
          <div className="wg-agentsw__group-title">
            {t('AGENT_SELECTOR_EXPLORE') || 'Explore a destination'}
          </div>
          {geoSuggestions.map(g => (
            <button
              key={g.agent_id}
              type="button"
              className="wg-agentsw__row"
              onClick={() => pickGuide(g.agent_id, g.locale || g.name)}
              data-testid={`suggest-${g.agent_id}`}
            >
              <span className="wg-agentsw__avatar wg-agentsw__avatar--pin">
                {g.avatar_image_url ? (
                  <img src={g.avatar_image_url} alt="" />
                ) : (
                  <PinIcon />
                )}
              </span>
              <span className="wg-agentsw__text">
                <span className="wg-agentsw__name">{g.name}</span>
                {!!g.locale && (
                  <span className="wg-agentsw__sub">{g.locale}</span>
                )}
              </span>
              <span className="wg-agentsw__state">
                <PlusIcon />
              </span>
            </button>
          ))}
        </section>
      )}

      {overlaySuggestions.length > 0 && (
        <section className="wg-agentsw__group">
          <div className="wg-agentsw__group-title">
            {t('AGENT_SELECTOR_EXPERIENCES') || 'Explore an experience'}
          </div>
          {overlaySuggestions.map(g => (
            <button
              key={g.agent_id}
              type="button"
              className="wg-agentsw__row"
              onClick={() => pickGuide(g.agent_id, g.name)}
              data-testid={`suggest-${g.agent_id}`}
            >
              <span className="wg-agentsw__avatar wg-agentsw__avatar--sparkles">
                {g.avatar_image_url ? (
                  <img src={g.avatar_image_url} alt="" />
                ) : (
                  <SparklesIcon />
                )}
              </span>
              <span className="wg-agentsw__text">
                <span className="wg-agentsw__name">{g.name}</span>
                {!!g.description && (
                  <span className="wg-agentsw__sub">{g.description}</span>
                )}
              </span>
              <span className="wg-agentsw__state">
                <PlusIcon />
              </span>
            </button>
          ))}
        </section>
      )}

      {isWorking && (
        <div className="wg-agentsw__loading">
          <Spinner size="small" />
        </div>
      )}

      {nothingToShow && !isWorking && (
        <div className="wg-agentsw__empty">
          <span>
            {query.trim()
              ? t('AGENT_SELECTOR_NO_RESULTS') ||
                'No saved guide or quick pick matches your search'
              : t('AGENT_SELECTOR_EMPTY') ||
                'Search for a destination to begin'}
          </span>
          {!!query.trim() && (
            <button
              type="button"
              className="wg-agentsw__explore-btn"
              onClick={() => addDestination(query)}
              data-testid="explore-query"
            >
              <PlusIcon /> {t('AGENT_SELECTOR_EXPLORE_QUERY') || 'Explore'} “
              {query.trim()}”
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default AgentSwitcherContent;

function SearchIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.5" y2="16.5" />
    </svg>
  );
}
function CheckIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
function PlusIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden
    >
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}
function PinIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 1118 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}
function SparklesIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12 2l2.4 6.6L21 11l-6.6 2.4L12 20l-2.4-6.6L3 11l6.6-2.4L12 2z" />
    </svg>
  );
}
